import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const toMin = (d) => {
  const p = String(d || "").split(":").map(Number);
  if (p.some(isNaN) || !p.length) return 0;
  return p.length === 3 ? p[0] * 60 + p[1] + p[2] / 60 : p.length === 2 ? p[0] + p[1] / 60 : p[0];
};

const missedLessons = (course) => (course.chapters || []).flatMap((ch) =>
  (ch.lessons || []).map((l, i) => ({ ...l, chapter: ch.number, after: ch.lessons[i - 1]?.title }))
    .filter((l) => l.tier === "premium" && !l.free_preview));

export const MissedPremiumRecap = ({ course, courseId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const key = `pms.missedPremium.dismissed.${courseId}`;
  const [hidden, setHidden] = useState(() => localStorage.getItem(key) === "1");
  const items = missedLessons(course);
  if (hidden || !items.length) return null;
  const minutes = Math.round(items.reduce((s, l) => s + toMin(l.duration), 0));
  const dismiss = () => { localStorage.setItem(key, "1"); setHidden(true); };
  return (
    <div data-testid="missed-premium-recap" className="relative overflow-hidden rounded-2xl bg-[#0b1220] p-7 sm:p-8 text-slate-200">
      <div className="pointer-events-none absolute -right-28 -top-28 h-80 w-80 rounded-full bg-amber-500/25 blur-3xl" />
      <div className="relative">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-400" data-testid="missed-premium-count">
          <Lock size={11} /> {t("missedPremium.badge", { count: items.length })}
        </div>
        <h3 className="mt-4 font-display text-2xl sm:text-3xl font-black leading-tight text-white">
          {minutes > 0 ? (
            <>{t("missedPremium.titleA")} <span className="text-amber-400">{t("missedPremium.minutes", { count: minutes })}</span> {t("missedPremium.titleB")}</>
          ) : t("missedPremium.titleNoTime")}
        </h3>
        <p className="mt-2 max-w-xl text-sm text-slate-400">{t("missedPremium.body", { count: items.length })}</p>
        <div className="mt-6 grid gap-2.5">
          {items.map((l) => (
            <div key={l.id} data-testid={`missed-premium-item-${l.id}`} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
              <div className="grid h-12 w-20 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-blue-900 to-slate-900">
                <Lock size={15} className="text-amber-400" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-50">{l.title}</div>
                <div className="mt-0.5 truncate text-xs text-slate-400">
                  {t("missedPremium.chapter", { n: l.chapter })}{l.after ? ` · ${t("missedPremium.after", { title: l.after })}` : ""}
                </div>
              </div>
              {l.duration && <div className="ml-auto font-mono text-xs text-slate-300">{l.duration}</div>}
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
          <span>✦ <b className="text-slate-200">{t("missedPremium.perkMastery")}</b></span>
          <span>✦ <b className="text-slate-200">{t("missedPremium.perkAll")}</b></span>
          <span>✦ <b className="text-slate-200">{t("missedPremium.perkProfessor")}</b></span>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <Button data-testid="missed-premium-upgrade" onClick={() => navigate("/checkout/agreement/premium")}
            className="h-12 gap-2 rounded-xl bg-amber-500 px-6 font-bold text-slate-950 hover:bg-amber-400">
            <Sparkles size={15} /> {t("missedPremium.cta", { count: items.length })} <ArrowRight size={15} />
          </Button>
          <button data-testid="missed-premium-dismiss" onClick={dismiss} className="text-sm text-slate-400 transition-colors hover:text-slate-200">
            {t("missedPremium.later")}
          </button>
        </div>
      </div>
    </div>
  );
};
